import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, X, MapPin, Package, DollarSign, TrendingUp, Truck, Edit2, Trash2, Loader2, Star } from 'lucide-react';
import { addToWishlist, checkWishlistStatus, removeFromWishlist } from '../../WishlistModal';

const GearEquipmentSection = ({ currentUser }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    condition: '',
    minPrice: '',
    maxPrice: '',
    location: '',
    sort: 'newest'
  });
  const [newListing, setNewListing] = useState({
    title: '',
    description: '',
    condition: 'good',
    category: 'turntables',
    price: '',
    negotiable: false,
    location: '',
    shipping_available: false,
    local_pickup: true,
    contact_method: 'message',
    contact_info: '',
    images: [],
    external_link: '',
    store_url: ''
  });

  // Fetch listings
  const fetchListings = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.category) params.append('category', filters.category);
      if (filters.condition) params.append('condition', filters.condition);
      if (filters.minPrice) params.append('min_price', filters.minPrice);
      if (filters.maxPrice) params.append('max_price', filters.maxPrice);
      if (filters.location) params.append('location', filters.location);
      if (searchQuery) params.append('search', searchQuery);
      params.append('sort', filters.sort);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/gear?${params.toString()}`);
      const data = await response.json();
      setListings(data);
    } catch (error) {
      console.error('Failed to fetch gear listings:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, searchQuery]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  // Handle listing creation
  const handleCreateListing = async () => {
    try {
      // Validation - external_link is now required
      if (!newListing.title || !newListing.description || !newListing.location || !newListing.price || !newListing.external_link) {
        alert('Please fill in all required fields including External Link');
        return;
      }
      
      // Validate email/phone if required
      if (newListing.contact_method === 'email' && !newListing.contact_info) {
        alert('Please enter your email address');
        return;
      }
      if (newListing.contact_method === 'phone' && !newListing.contact_info) {
        alert('Please enter your phone number');
        return;
      }
      
      const token = localStorage.getItem('beatseek_token');
      
      // Prepare listing data with proper types
      const listingData = {
        ...newListing,
        price: parseFloat(newListing.price) || 0,
        contact_info: newListing.contact_info || null,
        external_link: newListing.external_link || null,
        store_url: newListing.store_url || null
      };
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/gear`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(listingData)
      });

      if (response.ok) {
        setShowAddModal(false);
        setNewListing({
          title: '',
          description: '',
          condition: 'good',
          category: 'turntables',
          price: '',
          negotiable: false,
          location: '',
          shipping_available: false,
          local_pickup: true,
          contact_method: 'message',
          contact_info: '',
          images: [],
          external_link: '',
          store_url: ''
        });
        fetchListings();
        alert('Listing created successfully!');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create listing');
      }
    } catch (error) {
      console.error('Failed to create listing:', error);
      alert('Failed to create listing');
    }
  };

  // Handle edit listing
  const handleEditListing = (listing) => {
    setEditingListing({
      ...listing,
      price: listing.price?.toString() || ''
    });
    setShowEditModal(true);
  };

  // Handle update listing
  const handleUpdateListing = async () => {
    try {
      // Validation
      if (!editingListing.title || !editingListing.description || !editingListing.location || !editingListing.price || !editingListing.external_link) {
        alert('Please fill in all required fields including External Link');
        return;
      }

      const token = localStorage.getItem('beatseek_token');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/gear/${editingListing.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: editingListing.title,
          description: editingListing.description,
          condition: editingListing.condition,
          category: editingListing.category,
          price: parseFloat(editingListing.price) || 0,
          negotiable: editingListing.negotiable,
          location: editingListing.location,
          shipping_available: editingListing.shipping_available,
          local_pickup: editingListing.local_pickup,
          contact_method: editingListing.contact_method,
          contact_info: editingListing.contact_info,
          external_link: editingListing.external_link,
          store_url: editingListing.store_url
        })
      });

      if (response.ok) {
        setShowEditModal(false);
        setEditingListing(null);
        fetchListings();
        alert('Listing updated successfully!');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update listing');
      }
    } catch (error) {
      console.error('Failed to update listing:', error);
      alert('Failed to update listing');
    }
  };

  // Handle delete listing
  const handleDeleteListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) {
      return;
    }

    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/gear/${listingId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchListings();
        alert('Listing deleted successfully!');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete listing');
      }
    } catch (error) {
      console.error('Failed to delete listing:', error);
      alert('Failed to delete listing');
    }
  };

  // Handle listing click (track)
  const handleListingClick = async (listing) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/gear/${listing.id}`, {
        method: 'GET'
      });
    } catch (error) {
      console.error('Failed to track click:', error);
    }
  };

  // Check if user is logged in using correct localStorage key
  const isLoggedIn = !!localStorage.getItem('beatseek_user');

  return (
    <div className="mp-section" style={{ padding: '2rem', color: '#fff' }}>
      {/* Header with Add Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '2rem' }}>
        {isLoggedIn && (
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              background: 'none',
              border: 'none',
              color: '#00ffff',
              fontSize: '0.9rem',
              fontWeight: '500',
              cursor: 'pointer',
              padding: '0.5rem 1rem',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#ff00ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#00ffff';
            }}
          >
            + List Gear
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div style={{ marginBottom: '2rem' }}>
        {/* Search Bar */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={20} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255, 255, 255, 0.5)' }} />
            <input
              type="text"
              placeholder="Search gear..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 3rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
          </div>

          <select
            value={filters.sort}
            onChange={(e) => setFilters({...filters, sort: e.target.value})}
            style={{
              padding: '0.75rem 1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '1rem',
              cursor: 'pointer'
            }}
          >
            <option value="newest" style={{ background: '#1e293b', color: '#fff' }}>Newest First</option>
            <option value="oldest" style={{ background: '#1e293b', color: '#fff' }}>Oldest First</option>
            <option value="price-low" style={{ background: '#1e293b', color: '#fff' }}>Price: Low to High</option>
            <option value="price-high" style={{ background: '#1e293b', color: '#fff' }}>Price: High to Low</option>
            <option value="trending" style={{ background: '#1e293b', color: '#fff' }}>🔥 Trending</option>
          </select>
        </div>

        {/* Filter Pills */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <select
            value={filters.category}
            onChange={(e) => setFilters({...filters, category: e.target.value})}
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              color: '#fff',
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
          >
            <option value="" style={{ background: '#1e293b', color: '#fff' }}>All Categories</option>
            <option value="turntables" style={{ background: '#1e293b', color: '#fff' }}>Turntables</option>
            <option value="mixers" style={{ background: '#1e293b', color: '#fff' }}>Mixers</option>
            <option value="controllers" style={{ background: '#1e293b', color: '#fff' }}>Controllers</option>
            <option value="speakers" style={{ background: '#1e293b', color: '#fff' }}>Speakers</option>
            <option value="headphones" style={{ background: '#1e293b', color: '#fff' }}>Headphones</option>
            <option value="accessories" style={{ background: '#1e293b', color: '#fff' }}>Accessories</option>
            <option value="other" style={{ background: '#1e293b', color: '#fff' }}>Other</option>
          </select>

          <select
            value={filters.condition}
            onChange={(e) => setFilters({...filters, condition: e.target.value})}
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              color: '#fff',
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
          >
            <option value="" style={{ background: '#1e293b', color: '#fff' }}>All Conditions</option>
            <option value="new" style={{ background: '#1e293b', color: '#fff' }}>New</option>
            <option value="like-new" style={{ background: '#1e293b', color: '#fff' }}>Like New</option>
            <option value="good" style={{ background: '#1e293b', color: '#fff' }}>Good</option>
            <option value="fair" style={{ background: '#1e293b', color: '#fff' }}>Fair</option>
            <option value="for-parts" style={{ background: '#1e293b', color: '#fff' }}>For Parts</option>
          </select>

          <input
            type="number"
            placeholder="Min Price"
            value={filters.minPrice}
            onChange={(e) => setFilters({...filters, minPrice: e.target.value})}
            style={{
              width: '120px',
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              color: '#fff',
              fontSize: '0.9rem'
            }}
          />

          <input
            type="number"
            placeholder="Max Price"
            value={filters.maxPrice}
            onChange={(e) => setFilters({...filters, maxPrice: e.target.value})}
            style={{
              width: '120px',
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              color: '#fff',
              fontSize: '0.9rem'
            }}
          />

          <input
            type="text"
            placeholder="Location"
            value={filters.location}
            onChange={(e) => setFilters({...filters, location: e.target.value})}
            style={{
              width: '150px',
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              color: '#fff',
              fontSize: '0.9rem'
            }}
          />
        </div>
      </div>

      {/* Gear Listings - "Family Feud" Board Style */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255, 255, 255, 0.5)' }}>
          Loading gear...
        </div>
      ) : listings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255, 255, 255, 0.5)' }}>
          No gear listings found. Be the first to list!
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Smart sorting: listings with images first */}
          {listings
            .sort((a, b) => {
              const aHasImage = a.images && a.images.length > 0 && a.images[0].trim() !== '';
              const bHasImage = b.images && b.images.length > 0 && b.images[0].trim() !== '';
              
              if (aHasImage && !bHasImage) return -1;
              if (!aHasImage && bHasImage) return 1;
              return 0;
            })
            .map((listing) => (
              <GearListingCard 
                key={listing.id} 
                listing={listing} 
                currentUser={currentUser}
                onTrackClick={() => handleListingClick(listing)}
                onEdit={() => handleEditListing(listing)}
                onDelete={() => handleDeleteListing(listing.id)}
              />
            ))}
        </div>
      )}

      {/* Add Listing Modal */}
      {showAddModal && (
        <AddGearModal
          newListing={newListing}
          setNewListing={setNewListing}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleCreateListing}
        />
      )}

      {/* Edit Listing Modal */}
      {showEditModal && editingListing && (
        <AddGearModal
          newListing={editingListing}
          setNewListing={setEditingListing}
          onClose={() => {
            setShowEditModal(false);
            setEditingListing(null);
          }}
          onSubmit={handleUpdateListing}
          isEditing={true}
        />
      )}
    </div>
  );
};

// "Family Feud" Style Flippable Board Card
const GearListingCard = ({ listing, currentUser, onTrackClick, onEdit, onDelete }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [inWishlist, setInWishlist] = useState(false);
  const [wishlistItemId, setWishlistItemId] = useState(null);
  const [wishlistLoading, setWishlistLoading] = useState(false);

  // Check wishlist status on mount
  useEffect(() => {
    const checkStatus = async () => {
      const status = await checkWishlistStatus('gear', listing.id);
      setInWishlist(status.in_wishlist);
      setWishlistItemId(status.wishlist_item_id);
    };
    checkStatus();
  }, [listing.id]);

  const handleWishlistToggle = async (e) => {
    e.stopPropagation();
    setWishlistLoading(true);
    
    try {
      if (inWishlist && wishlistItemId) {
        const success = await removeFromWishlist(wishlistItemId);
        if (success) {
          setInWishlist(false);
          setWishlistItemId(null);
        }
      } else {
        const success = await addToWishlist('gear', {
          id: listing.id,
          title: listing.title,
          price: listing.price,
          condition: listing.condition,
          category: listing.category,
          location: listing.location,
          external_link: listing.external_link,
          images: listing.images
        });
        if (success) {
          setInWishlist(true);
          const status = await checkWishlistStatus('gear', listing.id);
          setWishlistItemId(status.wishlist_item_id);
        }
      }
    } finally {
      setWishlistLoading(false);
    }
  };

  const conditionColors = {
    'new': '#00ff00',
    'like-new': '#00ffff',
    'good': '#ffff00',
    'fair': '#ffa500',
    'for-parts': '#ff0000'
  };

  const conditionLabels = {
    'new': 'New',
    'like-new': 'Like New',
    'good': 'Good',
    'fair': 'Fair',
    'for-parts': 'For Parts'
  };

  const conditionColor = conditionColors[listing.condition] || '#888';
  const conditionLabel = conditionLabels[listing.condition] || listing.condition;
  
  // Check ownership using correct localStorage key
  const userData = localStorage.getItem('beatseek_user');
  const parsedUser = userData ? JSON.parse(userData) : null;
  const isOwner = parsedUser && parsedUser.id === listing.user_id;

  const handleFlip = (e) => {
    e.stopPropagation();
    setIsFlipped(!isFlipped);
    if (!isFlipped) {
      onTrackClick(); // Track view when flipping to details
    }
  };

  // Handle external link click
  const handleExternalLinkClick = (e) => {
    e.stopPropagation();
    if (listing.external_link) {
      window.open(listing.external_link, '_blank');
    }
  };

  return (
    <div
      style={{
        position: 'relative',
        height: '140px',
        perspective: '1000px',
        cursor: 'pointer'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Card Container */}
      <div
        onClick={handleFlip}
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          transition: 'transform 0.6s',
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateX(180deg)' : 'rotateX(0deg)'
        }}
      >
        {/* FRONT SIDE */}
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backfaceVisibility: 'hidden',
            padding: '1rem',
            background: isHovered
              ? 'linear-gradient(135deg, rgba(0, 255, 255, 0.2), rgba(255, 0, 255, 0.2))'
              : 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.9))',
            border: isHovered ? '2px solid #00ffff' : '2px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            boxShadow: isHovered ? '0 0 30px rgba(0, 255, 255, 0.3)' : 'none',
            transition: 'all 0.3s ease'
          }}
        >
          <div className="gear-listing-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '2rem', height: '100%' }}>
            {/* Left: Title and Details */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                  <h3
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      margin: 0,
                      color: '#fff',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {listing.title}
                  </h3>
                  {/* Condition Badge */}
                  <span
                    style={{
                      padding: '0.2rem 0.6rem',
                      background: `${conditionColor}22`,
                      border: `1px solid ${conditionColor}`,
                      borderRadius: '10px',
                      fontSize: '0.65rem',
                      color: conditionColor,
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}
                  >
                    {conditionLabel}
                  </span>
                  {/* Price Negotiable Badge */}
                  {listing.negotiable && (
                    <span
                      style={{
                        padding: '0.2rem 0.6rem',
                        background: 'rgba(0, 255, 255, 0.15)',
                        border: '1px solid rgba(0, 255, 255, 0.4)',
                        borderRadius: '10px',
                        fontSize: '0.65rem',
                        color: '#00ffff',
                        fontWeight: '600'
                      }}
                    >
                      💬 Negotiable
                    </span>
                  )}
                </div>

                <p
                  style={{
                    fontSize: '0.8rem',
                    color: 'rgba(255, 255, 255, 0.7)',
                    margin: '0 0 0.5rem 0',
                    lineHeight: '1.3',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {listing.description}
                </p>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <MapPin size={12} />
                    <span>{listing.location}</span>
                  </div>
                  {listing.shipping_available && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#00ffff' }}>
                      <Truck size={12} />
                      <span>Ships</span>
                    </div>
                  )}
                  {listing.click_count > 20 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#ff6b35' }}>
                      <TrendingUp size={12} />
                      <span>Hot</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Price and Category */}
            <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
              <div>
                <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#00ffff', marginBottom: '0.25rem' }}>
                  ${listing.price?.toLocaleString()}
                </div>
                <div style={{ 
                  fontSize: '0.7rem', 
                  color: 'rgba(255, 255, 255, 0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {listing.category}
                </div>
              </div>
              
              <div style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.4)' }}>
                Click to flip →
              </div>
            </div>
          </div>
        </div>

        {/* BACK SIDE */}
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backfaceVisibility: 'hidden',
            transform: 'rotateX(180deg)',
            padding: '0.75rem 1rem',
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(15, 23, 42, 0.95))',
            border: '2px solid #ff00ff',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            overflow: 'hidden'
          }}
        >
          {/* Seller Info */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
              <div>
                <div style={{ fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.5)', marginBottom: '0.15rem' }}>SELLER</div>
                <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#fff' }}>{listing.user_name}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.5)', marginBottom: '0.15rem' }}>CONTACT</div>
                <div style={{ fontSize: '0.75rem', color: '#00ffff' }}>
                  {listing.contact_method === 'message' ? 'In-App Message' : 
                   listing.contact_method === 'email' ? listing.contact_info : 
                   listing.contact_info}
                </div>
              </div>
            </div>

            {/* Shipping Info */}
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.5rem' }}>
              {listing.local_pickup && (
                <span style={{ fontSize: '0.7rem', color: '#00ff00' }}>✓ Pickup</span>
              )}
              {listing.shipping_available && (
                <span style={{ fontSize: '0.7rem', color: '#00ffff' }}>✓ Ships</span>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
            {/* Communication Links */}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {/* Contact Seller - single mailto link */}
              {listing.user_email && (
                <a
                  href={`mailto:${listing.user_email}?subject=Inquiry about ${listing.title} on BeatSeek&body=Hi ${listing.user_name},%0D%0A%0D%0AI saw your listing for "${listing.title}" on BeatSeek and I'm interested. Please send me more details or photos if available.%0D%0A%0D%0AThanks!`}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    color: '#00ffff',
                    fontSize: '0.8rem',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    transition: 'color 0.3s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#ff00ff'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#00ffff'}
                >
                  📧 Contact Seller
                </a>
              )}

              {/* Visit Store - conditional */}
              {listing.store_url && (
                <a
                  href={listing.store_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    color: '#00ffff',
                    fontSize: '0.8rem',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    transition: 'color 0.3s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#ff00ff'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#00ffff'}
                >
                  🏪 Visit Store
                </a>
              )}
            </div>

            {/* Action Buttons - Right Side */}
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              {/* Wishlist Star - Always show */}
              <button
                onClick={handleWishlistToggle}
                disabled={wishlistLoading}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: wishlistLoading ? 'wait' : 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: wishlistLoading ? 0.5 : 1,
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                title={inWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
              >
                {wishlistLoading ? (
                  <Loader2 size={16} style={{ color: '#ffd700' }} className="animate-spin" />
                ) : (
                  <Star 
                    size={18} 
                    style={{ 
                      color: inWishlist ? '#ffd700' : '#888',
                      fill: inWishlist ? '#ffd700' : 'transparent'
                    }} 
                  />
                )}
              </button>

              {isOwner ? (
                /* Compact Edit/Delete buttons for owner */
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit();
                    }}
                    style={{
                      padding: '0.3rem 0.6rem',
                      background: 'rgba(0, 255, 255, 0.15)',
                      border: '1px solid rgba(0, 255, 255, 0.4)',
                      borderRadius: '4px',
                      color: '#00ffff',
                      fontSize: '0.7rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <Edit2 size={11} />
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete();
                    }}
                    style={{
                      padding: '0.3rem 0.6rem',
                      background: 'rgba(255, 0, 0, 0.15)',
                      border: '1px solid rgba(255, 0, 0, 0.4)',
                      borderRadius: '4px',
                      color: '#ff4444',
                      fontSize: '0.7rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <Trash2 size={11} />
                    Del
                  </button>
                </>
              ) : listing.external_link ? (
                /* View Listing button for non-owners */
                <button
                  onClick={handleExternalLinkClick}
                  style={{
                    padding: '0.35rem 0.75rem',
                    background: 'linear-gradient(135deg, #ff00ff, #00ffff)',
                    border: 'none',
                    borderRadius: '4px',
                    color: '#fff',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  View Listing →
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add Gear Modal Component
const AddGearModal = ({ newListing, setNewListing, onClose, onSubmit, isEditing = false }) => {
  const [showImageInfoPopup, setShowImageInfoPopup] = useState(false);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'rgba(26, 26, 46, 0.98)',
          borderRadius: '20px',
          border: '2px solid rgba(0, 255, 255, 0.5)',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        <div style={{ padding: '2rem 2rem 0 2rem', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0, color: '#fff' }}>
              {isEditing ? 'Edit Gear Listing' : 'List Your Gear'}
            </h3>
            <button
              onClick={onClose}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'rgba(255, 0, 128, 0.2)',
                border: '1px solid rgba(255, 0, 128, 0.4)',
                color: '#ff0080',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Form - Scrollable */}
        <div style={{
          padding: '0 2rem 2rem 2rem',
          overflowY: 'auto',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          {/* Title */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
              Title *
            </label>
            <input
              type="text"
              value={newListing.title}
              onChange={(e) => setNewListing({...newListing, title: e.target.value})}
              placeholder="e.g., Pioneer CDJ-3000"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
          </div>

          {/* Description */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
              Description *
            </label>
            <textarea
              value={newListing.description}
              onChange={(e) => setNewListing({...newListing, description: e.target.value})}
              placeholder="Describe your gear..."
              rows={3}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Category and Condition Row */}
          <div className="gear-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                Category *
              </label>
              <select
                value={newListing.category}
                onChange={(e) => setNewListing({...newListing, category: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              >
                <option value="turntables" style={{ background: '#1e293b', color: '#fff' }}>Turntables</option>
                <option value="mixers" style={{ background: '#1e293b', color: '#fff' }}>Mixers</option>
                <option value="controllers" style={{ background: '#1e293b', color: '#fff' }}>Controllers</option>
                <option value="speakers" style={{ background: '#1e293b', color: '#fff' }}>Speakers</option>
                <option value="headphones" style={{ background: '#1e293b', color: '#fff' }}>Headphones</option>
                <option value="accessories" style={{ background: '#1e293b', color: '#fff' }}>Accessories</option>
                <option value="other" style={{ background: '#1e293b', color: '#fff' }}>Other</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                Condition *
              </label>
              <select
                value={newListing.condition}
                onChange={(e) => setNewListing({...newListing, condition: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              >
                <option value="new" style={{ background: '#1e293b', color: '#fff' }}>New</option>
                <option value="like-new" style={{ background: '#1e293b', color: '#fff' }}>Like New</option>
                <option value="good" style={{ background: '#1e293b', color: '#fff' }}>Good</option>
                <option value="fair" style={{ background: '#1e293b', color: '#fff' }}>Fair</option>
                <option value="for-parts" style={{ background: '#1e293b', color: '#fff' }}>For Parts</option>
              </select>
            </div>
          </div>

          {/* Price and Location Row */}
          <div className="gear-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                Price (USD) *
              </label>
              <input
                type="number"
                step="0.01"
                value={newListing.price}
                onChange={(e) => setNewListing({...newListing, price: e.target.value})}
                placeholder="0.00"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                Location *
              </label>
              <input
                type="text"
                value={newListing.location}
                onChange={(e) => setNewListing({...newListing, location: e.target.value})}
                placeholder="City, State"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              />
            </div>
          </div>

          {/* External Link - REQUIRED */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
              External Link (Reverb, eBay, etc.) *
            </label>
            <input
              type="url"
              value={newListing.external_link}
              onChange={(e) => setNewListing({...newListing, external_link: e.target.value})}
              placeholder="https://reverb.com/item/..."
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
          </div>

          {/* Store URL - Optional */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
              Your Store URL (Optional)
            </label>
            <input
              type="url"
              value={newListing.store_url || ''}
              onChange={(e) => setNewListing({...newListing, store_url: e.target.value})}
              placeholder="https://yourstore.com"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
          </div>

          {/* Contact Method */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
              Preferred Contact Method
            </label>
            <select
              value={newListing.contact_method}
              onChange={(e) => setNewListing({...newListing, contact_method: e.target.value})}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem'
              }}
            >
              <option value="message" style={{ background: '#1e293b', color: '#fff' }}>In-App Message</option>
              <option value="email" style={{ background: '#1e293b', color: '#fff' }}>Email</option>
              <option value="phone" style={{ background: '#1e293b', color: '#fff' }}>Phone</option>
            </select>
          </div>

          {/* Contact Info (conditional) */}
          {(newListing.contact_method === 'email' || newListing.contact_method === 'phone') && (
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                {newListing.contact_method === 'email' ? 'Email Address *' : 'Phone Number *'}
              </label>
              <input
                type={newListing.contact_method === 'email' ? 'email' : 'tel'}
                value={newListing.contact_info}
                onChange={(e) => setNewListing({...newListing, contact_info: e.target.value})}
                placeholder={newListing.contact_method === 'email' ? 'your@email.com' : '(555) 123-4567'}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              />
            </div>
          )}

          {/* Checkboxes Row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'rgba(255, 255, 255, 0.8)' }}>
              <input
                type="checkbox"
                checked={newListing.negotiable}
                onChange={(e) => setNewListing({...newListing, negotiable: e.target.checked})}
                style={{ width: '18px', height: '18px', accentColor: '#00ffff' }}
              />
              Price Negotiable
            </label>
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'rgba(255, 255, 255, 0.8)' }}>
              <input
                type="checkbox"
                checked={newListing.local_pickup}
                onChange={(e) => setNewListing({...newListing, local_pickup: e.target.checked})}
                style={{ width: '18px', height: '18px', accentColor: '#00ffff' }}
              />
              Local Pickup
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'rgba(255, 255, 255, 0.8)' }}>
              <input
                type="checkbox"
                checked={newListing.shipping_available}
                onChange={(e) => setNewListing({...newListing, shipping_available: e.target.checked})}
                style={{ width: '18px', height: '18px', accentColor: '#00ffff' }}
              />
              Shipping Available
            </label>
          </div>

          {/* Image URL - Optional with info popup */}
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
              Product Image URL (Optional)
              <button
                type="button"
                onClick={() => setShowImageInfoPopup(!showImageInfoPopup)}
                onMouseEnter={() => setShowImageInfoPopup(true)}
                onMouseLeave={() => setShowImageInfoPopup(false)}
                style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  background: 'rgba(0, 255, 255, 0.2)',
                  border: '1px solid rgba(0, 255, 255, 0.4)',
                  color: '#00ffff',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}
              >
                i
              </button>
            </label>
            {showImageInfoPopup && (
              <div style={{
                padding: '0.75rem',
                background: 'rgba(0, 255, 255, 0.1)',
                border: '1px solid rgba(0, 255, 255, 0.3)',
                borderRadius: '8px',
                marginBottom: '0.5rem',
                fontSize: '0.85rem',
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: '1.5'
              }}>
                <strong>📸 How to add a product image:</strong>
                <br/><br/>
                → Right-click your product image
                <br/>→ Select &quot;Copy image address&quot;
                <br/>→ Paste the URL here
                <br/><br/>
                Example: https://i.imgur.com/abc123.jpg
              </div>
            )}
            <input
              type="url"
              value={newListing.images?.[0] || ''}
              onChange={(e) => setNewListing({...newListing, images: e.target.value ? [e.target.value] : []})}
              placeholder="https://example.com/image.jpg"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
          </div>

          {/* Submit Button */}
          <button
            onClick={onSubmit}
            style={{
              padding: '0.85rem 1.5rem',
              background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              marginTop: '0.5rem'
            }}
          >
            {isEditing ? 'Update Listing' : 'Create Listing'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GearEquipmentSection;
